/*
 * Nightglow Collapsible Content
 * ------------------------------------------------------------
 * Adds smooth open/close animation and accordion behavior to the
 * built-in Dawn collapsible-content section when the Nightglow
 * design checkbox is enabled.
 */

(() => {
  if (window.NightglowCollapsibleContent) {
    window.NightglowCollapsibleContent.init(document);
    return;
  }

  const DURATION = 360;
  const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const initializedSections = new WeakSet();

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class NightglowAccordionGroup {
    constructor(root) {
      this.root = root;
      this.items = Array.from(root.querySelectorAll('[data-nightglow-details]')).map((details) => ({
        details,
        summary: details.querySelector('summary'),
        content: details.querySelector('[data-nightglow-content]'),
      }));

      this.queue = Promise.resolve();
      this.normalizeOpenState();
      this.bindEvents();
    }

    normalizeOpenState() {
      let openItemFound = false;

      this.items.forEach((item) => {
        if (!item.details.open) return;

        if (openItemFound) {
          item.details.open = false;
          return;
        }

        openItemFound = true;
      });
    }

    bindEvents() {
      this.items.forEach((item) => {
        if (!item.summary || !item.content) return;

        item.summary.addEventListener('click', (event) => {
          event.preventDefault();

          this.queue = this.queue
            .then(() => this.toggle(item))
            .catch(() => this.resetItem(item));
        });
      });
    }

    async toggle(item) {
      if (item.details.open) {
        await this.close(item);
        return;
      }

      const openItem = this.items.find(
        (candidate) => candidate !== item && candidate.details.open
      );

      if (openItem) {
        await this.close(openItem);
      }

      await this.open(item);
    }

    open(item) {
      const { details, content } = item;

      details.classList.remove('is-closing');

      if (prefersReducedMotion()) {
        details.open = true;
        this.resetContentStyles(content);
        return Promise.resolve();
      }

      const endMargin = this.getContentGap(content);

      content.style.height = '0px';
      content.style.opacity = '0';
      content.style.marginTop = '0px';
      content.style.overflow = 'hidden';
      details.open = true;

      const endHeight = content.scrollHeight;

      return new Promise((resolve) => {
        const animation = content.animate(
          [
            { height: '0px', opacity: 0, marginTop: '0px' },
            { height: `${endHeight}px`, opacity: 1, marginTop: endMargin },
          ],
          {
            duration: DURATION,
            easing: EASING,
          }
        );

        animation.onfinish = () => {
          this.resetContentStyles(content);
          resolve();
        };

        animation.oncancel = () => {
          this.resetContentStyles(content);
          resolve();
        };
      });
    }

    close(item) {
      const { details, content } = item;

      if (!details.open) return Promise.resolve();

      if (prefersReducedMotion()) {
        details.open = false;
        details.classList.remove('is-closing');
        this.resetContentStyles(content);
        return Promise.resolve();
      }

      details.classList.add('is-closing');

      const startHeight = content.getBoundingClientRect().height;
      const startMargin = this.getContentGap(content);
      content.style.overflow = 'hidden';

      return new Promise((resolve) => {
        const animation = content.animate(
          [
            { height: `${startHeight}px`, opacity: 1, marginTop: startMargin },
            { height: '0px', opacity: 0, marginTop: '0px' },
          ],
          {
            duration: DURATION,
            easing: EASING,
          }
        );

        animation.onfinish = () => {
          details.open = false;
          details.classList.remove('is-closing');
          this.resetContentStyles(content);
          resolve();
        };

        animation.oncancel = () => {
          details.open = false;
          details.classList.remove('is-closing');
          this.resetContentStyles(content);
          resolve();
        };
      });
    }

    getContentGap(content) {
      return window.getComputedStyle(content).marginTop || '0px';
    }

    resetContentStyles(content) {
      content.style.removeProperty('height');
      content.style.removeProperty('opacity');
      content.style.removeProperty('margin-top');
      content.style.removeProperty('overflow');
    }

    resetItem(item) {
      item.details.classList.remove('is-closing');
      this.resetContentStyles(item.content);
    }
  }

  const init = (scope = document) => {
    const roots = [];

    if (scope.matches?.('[data-nightglow-collapsible]')) {
      roots.push(scope);
    }

    roots.push(...scope.querySelectorAll?.('[data-nightglow-collapsible]') || []);

    roots.forEach((root) => {
      if (initializedSections.has(root)) return;

      initializedSections.add(root);
      new NightglowAccordionGroup(root);
    });
  };

  window.NightglowCollapsibleContent = { init };

  init(document);

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
